import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { ArrowLeft, Wrench, MapPin, Leaf, Calendar, QrCode, Trash2, Pencil } from "lucide-react";
import MachineBookingModal from "../components/machines/MachineBookingModal";
import AttachToProject from "../components/machines/AttachToProject";
import QrPanel from "../components/QrPanel";
import VisibilityPicker from "../components/VisibilityPicker";
import StatusBadge from "../components/StatusBadge";
import NextActionsPanel from "../components/NextActionsPanel";

const BK_LABELS = { bokad: "Bokad", godkand: "Godkand", utlamnad: "Utlamnad", aterlamnad: "Aterlamnad", avbruten: "Avbruten" };

export default function MachineDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [machine, setMachine] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showBook, setShowBook] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [justBooked, setJustBooked] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: bk }] = await Promise.all([
        api.get(`/machines/${id}`),
        api.get(`/machine-bookings`, { params: { machine_id: id } }),
      ]);
      setMachine(m);
      setBookings(bk || []);
    } catch {
      toast.error(t("detail.notFound"));
      navigate("/machines");
    }
  }, [id, navigate, t]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;
  if (!machine) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;

  const isOwner = machine.owner_id === user.user_id || user.role === "admin";
  const co2e = machine.co2e_indikator || {};
  const url = `${window.location.origin}/machines/${machine.id}`;

  const setStatus = async (bid, status) => {
    try {
      await api.patch(`/machine-bookings/${bid}/status`, { status });
      toast.success(t(`mbk.status${BK_LABELS[status]}`));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const del = async () => {
    if (!confirm(t("mach.confirmDelete"))) return;
    try {
      await api.delete(`/machines/${machine.id}`);
      toast.success(t("mach.deleted"));
      navigate("/machines");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1100px] mx-auto px-6 pt-8 pb-16">
        <Link to="/machines" className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid="machine-back-link">
          <ArrowLeft size={12} /> {t("nav.backToList")}
        </Link>

        <div className="mt-4 grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <div className="col-span-12 lg:col-span-7 bg-[var(--bone)] p-6">
            <div className="flex items-center gap-2 label-tech"><Wrench size={12} /> {machine.typ}</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">{machine.namn}</h1>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-[var(--ink-700)]">
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {machine.plats || "—"}</span>
              <StatusBadge status={machine.status} />
              {machine.ansvarig && <span className="label-tech">{t("mach.ansvarig")}: {machine.ansvarig}</span>}
              {machine.organization_id && machine.organization_id !== user.organization_id && (
                <span className="tag bg-[var(--brick)] text-[var(--bone)]" data-testid="external-resource-tag">{t("vis.externalTag")}</span>
              )}
              {machine.visibility === "shared" && (
                <span className="tag bg-[var(--moss)] text-[var(--bone)]" data-testid="shared-tag">{t("vis.sharedTag")}</span>
              )}
              {machine.visibility === "public" && (
                <span className="tag" data-testid="public-tag">{t("vis.publicTag")}</span>
              )}
            </div>

            <div className="mt-6 p-4 border border-[var(--moss)] bg-[var(--moss)] text-[var(--bone)]" data-testid="machine-co2e-info">
              <div className="label-tech opacity-80 inline-flex items-center gap-2"><Leaf size={12} /> {t("mbk.savings")}</div>
              <div className="font-display text-2xl mt-1">~{co2e.co2e_besparing_kg ?? 0} kg CO2e</div>
              <div className="label-tech opacity-80 mt-1">{t("mbk.savingsCompare")} (~{co2e.co2e_kop_kg ?? 0} kg)</div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <KV k={t("mach.driftstimmar")} v={machine.driftstimmar ?? 0} />
              <KV k={t("mach.serviceintervall")} v={machine.serviceintervall_h ?? "—"} />
              <KV k={t("mach.co2eKop")} v={`${machine.generisk_co2e_kop ?? 0} kg`} />
              <KV k={t("mach.co2ePerKm")} v={`${machine.generisk_co2e_transport_per_km ?? 0.25} kg`} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button className="btn-primary" onClick={() => setShowBook(true)} data-testid="machine-book-btn">
                <Calendar size={14} /> {t("mach.bookBtn")}
              </button>
              <button className="btn-ghost" onClick={() => setShowQr(!showQr)} data-testid="machine-qr-btn">
                <QrCode size={14} /> {t("qr.title")}
              </button>
              {isOwner && (
                <button className="btn-ghost" onClick={() => navigate(`/machines?edit=${machine.id}`)} data-testid="machine-edit-btn">
                  <Pencil size={14} /> {t("detail.edit")}
                </button>
              )}
              {isOwner && (
                <button className="btn-ghost text-[var(--brick)]" onClick={del} data-testid="machine-delete-btn">
                  <Trash2 size={14} /> {t("mach.deleted")}
                </button>
              )}
            </div>

            {justBooked && (
              <div className="mt-6">
                <NextActionsPanel
                  resourceType="machine"
                  resourceId={machine.id}
                  onShare={() => {
                    setJustBooked(false);
                    setTimeout(() => {
                      const el = document.querySelector('[data-testid="visibility-picker"]');
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 50);
                  }}
                />
              </div>
            )}

            <div className="mt-6">
              <AttachToProject objectType="machine" objectId={machine.id} currentProjectId={machine.project_id} onChange={load} />
            </div>

            {isOwner && (
              <div className="mt-4">
                <VisibilityPicker
                  resourceType="machine"
                  resourceId={machine.id}
                  currentVisibility={machine.visibility}
                  currentSharedWith={machine.shared_with}
                  onChange={load}
                />
              </div>
            )}

            {showQr && (
              <div className="mt-6">
                <QrPanel
                  defaultMode="detail"
                  resourceName={machine.namn}
                  urls={{
                    detail: { url, label: t("qr.modeDetail"), hint: t("qr.scanHint") },
                    quickbook: { url: `${window.location.origin}/machines/${machine.id}/quickbook`, label: t("qr.modeQuickBook"), hint: t("qr.scanHintQuickBook") },
                  }}
                />
              </div>
            )}
          </div>

          {/* Bookings sidebar */}
          <div className="col-span-12 lg:col-span-5 bg-[var(--surface)] p-6">
            <div className="label-tech">{t("mbk.title")}</div>
            <div className="mt-3 divide-y divide-[var(--ink-200)]">
              {bookings.length === 0 && <div className="label-tech py-3">{t("mbk.empty")}</div>}
              {bookings.map((b) => (
                <BookingRow key={b.id} b={b} t={t} setStatus={setStatus} canManage={isOwner || b.user_id === user.user_id} isOwnerView={isOwner} currentUserId={user.user_id} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {showBook && <MachineBookingModal machine={machine} onClose={() => setShowBook(false)} onCreated={() => { setShowBook(false); setJustBooked(true); load(); }} />}
    </Layout>
  );
}

function KV({ k, v }) {
  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-3">
      <div className="label-tech">{k}</div>
      <div className="font-display text-base mt-1">{String(v)}</div>
    </div>
  );
}

function BookingRow({ b, t, setStatus, canManage, isOwnerView, currentUserId }) {
  const fmt = (s) => new Date(s).toLocaleString();
  const isBooker = b.user_id === currentUserId;
  const isInternal = Boolean(b.is_internal);
  // For internal bookings the booker themselves can run the full lifecycle
  // (pickup + return) without owner approval.
  const canPickup = isOwnerView || (isBooker && isInternal);
  return (
    <div className="py-3" data-testid={`booking-row-${b.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display">{b.bokare_namn || b.user_name}</div>
          <div className="label-tech">
            {fmt(b.start)} → {fmt(b.slut)}
            {b.plats_text ? <> · {b.plats_text}</> : null}
            {b.bokare_namn && b.bokare_namn !== b.user_name ? <> · ({b.user_name})</> : null}
          </div>
        </div>
        <span className="tag tag-moss">{t(`mbk.status${BK_LABELS[b.status]}`)}</span>
      </div>
      {b.beraknad_co2e_besparing_kg ? (
        <div className="label-tech mt-1">~{b.beraknad_co2e_besparing_kg} kg CO2e ({b.transport_km} km)</div>
      ) : null}
      {canManage && (
        <div className="mt-2 flex flex-wrap gap-2">
          {isOwnerView && b.status === "bokad" && <button className="btn-ghost" onClick={() => setStatus(b.id, "godkand")} data-testid={`approve-${b.id}`}>{t("mbk.approve")}</button>}
          {canPickup && b.status === "godkand" && <button className="btn-ghost" onClick={() => setStatus(b.id, "utlamnad")} data-testid={`pickup-${b.id}`}>{t("mbk.pickup")}</button>}
          {b.status === "utlamnad" && <button className="btn-ghost" onClick={() => setStatus(b.id, "aterlamnad")} data-testid={`return-${b.id}`}>{t("mbk.return")}</button>}
          {(b.status === "bokad" || b.status === "godkand") && <button className="btn-ghost text-[var(--brick)]" onClick={() => setStatus(b.id, "avbruten")} data-testid={`cancel-${b.id}`}>{t("mbk.cancel")}</button>}
        </div>
      )}
    </div>
  );
}
